import { useSyncExternalStore } from 'react';

// Live property tweaks, made in drill mode and thrown away on refresh.
//
// The read half of drill mode already tells you what a component is made of and
// what its call site passes. This is the other half: change a value, see it
// immediately, and when the screen looks right, copy the whole session out as one
// instruction for Claude to apply to the source properly.
//
// Deliberately not written to disk. A tweak here is a question — does this want
// more padding — and half-answered questions landing in the files is the failure
// mode of every in-browser editor. The copy-out is the commit.
//
// Values are tokens, never raw CSS. A stepper picks `p={3}`, not `16px`, and a
// colour picker offers `custom.neutral.100`, not a hex — which is the difference
// between a tweak you can paste as an instruction and one you have to translate.
// The px shown beside a step is a readout, not the value.
//
// A module store rather than a context, so the five stages that already mount
// InspectProvider need no wiring, and a tweak survives the panel unmounting when
// the selection changes.

// What kind of thing is being changed, which decides how it is applied and how it
// reads in the copied instruction.
// `order` is a reorder made by dragging a child along its flex parent; it is
// applied with CSS `order` on the siblings, so React's tree never changes.
export type TweakKind = 'spacing' | 'colour' | 'typography' | 'order';

// A declaration to write. `element` when it lands somewhere other than the
// tweak's own element: a reorder writes `order` onto every sibling, and a gap on
// a margin-spaced Stack zeroes each child's margin.
export type TweakCss = {
  property: string;
  value: string;
  element?: HTMLElement;
};

export type Tweak = {
  id: string;
  // The component the tweak was made on, for the instruction. Not an identifier:
  // two instances of a card share it, and the instruction says which screen.
  component: string;
  // The source file, when the registry knows it.
  file?: string;
  kind: TweakKind;
  // The prop as it would be written at the call site: 'p', 'gap', 'variant',
  // 'bgcolor'.
  prop: string;
  // Token in, token out. `from` is what the call site had, read off the fiber or
  // the computed style; both are shown in the instruction so a wrong reading is
  // visible rather than silently applied.
  from: string;
  to: string;
  // Said under the line in the instruction, when the change is made somewhere
  // other than where it looks: a reorder of mapped children is an array edit.
  note?: string;
  // Whether the element was rendering something before the tweak, from wherever
  // it came: the call site, the component, the theme. A change to 0 is only an
  // instruction when it is taking something away.
  overrides?: boolean;
  // The element it was applied to, and the inline value that was there before, so
  // a reset puts back exactly what it found rather than clearing the property.
  element: HTMLElement;
  restore: TweakCss[];
};

let tweaks: Tweak[] = [];
const listeners = new Set<() => void>();

const emit = () => {
  tweaks = [...tweaks];
  listeners.forEach((l) => l());
};

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

const snapshot = () => tweaks;

export const useTweaks = () => useSyncExternalStore(subscribe, snapshot);

// Apply a set of CSS declarations to an element, remembering what was there.
//
// Inline style rather than a stylesheet rule: the panel is editing one instance a
// person is pointing at, and a rule would catch every sibling that happens to
// share a class.
export const applyTweak = (
  input: Omit<Tweak, 'id' | 'restore'> & { css: TweakCss[] },
): void => {
  const { css, ...rest } = input;
  const restore = css.map(({ property, element }) => ({
    property,
    element,
    value: (element ?? rest.element).style.getPropertyValue(property),
  }));
  css.forEach(({ property, value, element }) =>
    (element ?? rest.element).style.setProperty(property, value),
  );

  // One tweak per element and prop. Editing padding twice is one decision with a
  // final value, not two entries in the instruction — but the `from` and the
  // original inline value are kept from the first, so the instruction still says
  // what the call site actually had and a reset still lands back there.
  const existing = tweaks.find(
    (t) => t.element === rest.element && t.prop === rest.prop,
  );
  if (existing) {
    tweaks = tweaks.map((t) =>
      t === existing ? { ...existing, to: rest.to } : t,
    );
  } else {
    tweaks = [
      ...tweaks,
      { ...rest, id: `${Date.now()}-${rest.prop}`, restore },
    ];
  }
  emit();
};

const undo = (t: Tweak) =>
  t.restore.forEach(({ property, value, element }) =>
    value
      ? (element ?? t.element).style.setProperty(property, value)
      : (element ?? t.element).style.removeProperty(property),
  );

export const resetTweak = (id: string): void => {
  const t = tweaks.find((x) => x.id === id);
  if (!t) return;
  undo(t);
  tweaks = tweaks.filter((x) => x !== t);
  emit();
};

export const resetAllTweaks = (): void => {
  tweaks.forEach(undo);
  tweaks = [];
  emit();
};

// What was tweaked, as an instruction. Follows the Loft: command pattern — Loft
// is read-only, so the button copies words rather than writing files.
//
// Grouped by component and file because that is how the change gets made: one
// file open, several props to move. Every line carries both values, so a reading
// this panel got wrong is caught before anything is edited.
// The tweaks worth sending. A drag that ended where it started is nothing, and a
// 0 set where nothing was rendering is nothing either: an instruction to write
// `pt={0}` onto a call site that never had padding is a line somebody has to read
// and then not do.
export const meaningfulTweaks = (list: Tweak[]): Tweak[] =>
  list.filter(
    (t) =>
      t.from !== t.to &&
      !(t.to === '0' && (t.from === 'unset' || t.from === '0') && !t.overrides),
  );

export const tweakCommand = (all: Tweak[]): string => {
  const list = meaningfulTweaks(all);
  if (list.length === 0) return '';
  const groups = new Map<string, Tweak[]>();
  for (const t of list) {
    const key = `${t.component}${t.file ? ` (${t.file})` : ''}`;
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  const body = [...groups.entries()]
    .map(
      ([key, ts]) =>
        `${key}\n` +
        ts
          .map(
            (t) =>
              `  - ${t.prop}: ${t.from} → ${t.to}` +
              (t.note ? `\n    ${t.note}` : ''),
          )
          .join('\n'),
    )
    .join('\n');
  const reordered = list.some((t) => t.kind === 'order');
  return (
    `Loft: apply these tweaks from drill mode\n\n${body}\n\n` +
    "These were made live in the browser against the theme's tokens. Apply them at the call site as props, not as sx." +
    (reordered
      ? ' An order line lists the children before and after: move them in the source, and drop any CSS order.'
      : '')
  );
};
